import { inject, Injectable } from "@angular/core";
import { Notification } from "app/layout/common/notifications/notifications.types";
import { ApiResponse } from "app/shared/models";
import { HttpClientService } from "app/shared/services";
import { map, Observable, ReplaySubject, switchMap, take, tap } from "rxjs";

@Injectable({ providedIn: "root" })
export class NotificationsService {
  private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);
  private _unRead: ReplaySubject<number> = new ReplaySubject<number>(1);
  private _clientService = inject(HttpClientService);

  constructor() {}

  get notifications$(): Observable<Notification[]> {
    return this._notifications.asObservable();
  }

  get unRead$(): Observable<number> {
    return this._unRead.asObservable();
  }

  getAll(): Observable<ApiResponse<Notification[]>> {
    return this._clientService.get<ApiResponse<Notification[]>>("/webapp/notifications").pipe(
      tap((response) => {
        this._notifications.next(response.data);
        this._unRead.next(this._calculateUnreadCount(response.data));
      })
    );
  }

  create(notification: Notification): Observable<Notification> {
    return this.notifications$.pipe(
      take(1),
      switchMap(() =>
        this._clientService.post<Notification>("/webapp/notifications", { notification }).pipe(
          map((newNotification) => {
            return newNotification;
          })
        )
      )
    );
  }

  update(id: string, notification: Notification): Observable<Notification> {
    return this.notifications$.pipe(
      take(1),
      switchMap(() =>
        this._clientService
          .put<Notification>(`/webapp/notifications/${id}`, {
            read: notification.read,
          })
          .pipe(
            map((updatedNotification: Notification) => {
              // Return the updated notification
              return updatedNotification;
            })
          )
      )
    );
  }

  delete(id: string): Observable<boolean> {
    return this.notifications$.pipe(
      take(1),
      switchMap(() =>
        this._clientService.delete<boolean>(`/webapp/notifications/${id}`).pipe(
          map((isDeleted: boolean) => {
            // Return the deleted status
            return isDeleted;
          })
        )
      )
    );
  }

  private _calculateUnreadCount(_notificatons: Notification[]): number {
    let count = 0;

    if (_notificatons && _notificatons.length) {
      count = _notificatons.filter((notification) => !notification.read).length;
    }

    return count;
  }

  markAllAsRead(): Observable<boolean> {
    return this.notifications$.pipe(
      take(1),
      switchMap(() =>
        this._clientService.post("/webapp/notifications/mark-all").pipe(
          map(() => {
            return true;
          })
        )
      )
    );
  }
}
